<script lang="ts">
    import Vue from "vue";
    import Component from 'vue-class-component';
    import { Spreadsheet, SpreadsheetSheet } from '@progress/kendo-spreadsheet-vue-wrapper';
    import { toEntityValuesString } from "@common/Common";
    import { KendoSpreadsheetColumn, KendoSpreadsheetValueSerie } from "@Kendo/Components/Spreadsheet/types";
    import { KendoSpreadsheetUtils } from "@Kendo/Components/Spreadsheet/utils";

    @Component({
        components: {
            Spreadsheet,
            SpreadsheetSheet
        },
        props: {
            propTitle: { type: String, required: true },
            propSeries: { type: Array, required: true },
            propColumns: { type: Array, required: true },
        }
    })
    export default class EditableValueSeriesSheet extends Vue
    {
        get title(): string { return this.$props.propTitle; }
        get series(): KendoSpreadsheetValueSerie[] { return this.$props.propSeries; }
        get columns(): KendoSpreadsheetColumn[] { return this.$props.propColumns; }

        initialized: boolean = false;

        mounted()
        {
            this.$nextTick(() =>
            {
                const spreadsheet = (this.$refs.spreadsheet as any).kendoWidget() as kendo.ui.Spreadsheet;

                const totalRows = this.series.length + 1;
                const totalCols = this.columns.length + 1;

                const rows = [];

                // Header row
                const headerRow = {
                    cells: [{ value: this.title, bold: true }]
                };

                this.columns.forEach((col, idx) =>
                {
                    headerRow.cells[idx + 1] = { value: col.Label, bold: true };
                });

                rows.push(headerRow);

                // Data rows
                for (let row = 0; row < this.series.length; row++)
                {
                    const cells = [{ value: this.series[row].Name }];
                    this.series[row].Values.forEach(val =>
                    {
                        cells.push({ value: val });
                    });
                    rows.push({ cells });
                }

                // Apply the data
                spreadsheet.fromJSON({
                    sheets: [{
                        name: this.title,
                        rows
                    }]
                });

                // Fetch sheet AGAIN after fromJSON
                const sheet = spreadsheet.activeSheet();

                // Freeze headers
                sheet.frozenRows(1);
                sheet.frozenColumns(1);

                // Disable headers
                sheet.range(`A1:${KendoSpreadsheetUtils.columnToLetter(totalCols)}1`).enable(false);
                sheet.range(`A1:A${totalRows}`).enable(false);

                const topLeft = "B2";
                const bottomRight = `${KendoSpreadsheetUtils.columnToLetter(totalCols)}${totalRows}`;
                const editableRange = `${topLeft}:${bottomRight}`;

                sheet.range(editableRange).validation({
                    dataType: "number",
                    comparerType: "custom",
                    from: "ISNUMBER(B2)", // top-left of range — Kendo adjusts automatically
                    allowNulls: true,
                    type: "reject",
                    messageTemplate: "Only numeric values are allowed."
                });

                this.initialized = true;
            });
        }

        onChange()
        {
            if (!this.initialized) return;
            this.$emit('dataChange');
        }

        getEntriesForSave(): string[]
        {
            const spreadsheet = (this.$refs.spreadsheet as any).kendoWidget() as kendo.ui.Spreadsheet;
            const sheetJson = spreadsheet.toJSON().sheets[0];

            const transformedData = sheetJson.rows
                .map((row: any) =>
                {
                    // Create an array for the row, initializing all values to empty string
                    const rowValues = Array(this.columns.length).fill('');

                    // Populate the array with actual data from the cells
                    (row.cells || []).forEach((cell: any) =>
                    {
                        rowValues[cell.index] = cell.value ?? '';
                    });

                    // Find the corresponding series item by the first cell's value (name). Assuming the name is in the first column
                    const seriesItem = this.series.find(s => s.Name === rowValues[0]);

                    return {
                        Id: seriesItem ? seriesItem.Id : null,
                        Values: rowValues.slice(1) // Exclude the first cell which is the name
                    };
                })
                .filter(a => a.Id != null)
                .map(a => toEntityValuesString("", a.Id, a.Values));

            return transformedData;
        }
    }

</script>

<template>
    <div>
        <spreadsheet ref="spreadsheet" 
                     class="spreadsheet-container" 
                     :use-culture-decimals="false" 
                     :sheetsbar="false" 
                     :toolbar="false" 
                     :columns="columns.length + 1"
                     :rows="series.length + 1"
                     :column-width="150"
                     :row-height="25"
                     @change="onChange">
            
            <spreadsheet-sheet :name="title"></spreadsheet-sheet>
        </spreadsheet>
    </div>
</template>      


<style scoped>
    /* Style for the spreadsheet container */
    .spreadsheet-container {
        width: 100%;
        /* Add more styles as needed */
    }


    /* Style for the Kendo Spreadsheet */
    .k-spreadsheet {
        width: 100%;
        /* Adjust height as needed */
    }
</style>